// simulationTimer.js - Standalone simulation timer module
import { apiBaseUrl } from '../../backend/config.js';
import { showNotification, updateTotalPointsDisplay, showPopupPoints, clearLEDDisplay } from './notif.js';
import { connectWebSocket, socket, messageQueue } from './socketU.js';
import { getAuthToken } from './auth.js';

// ────── TIMER STATE MANAGEMENT ──────
let simulationTimer = null;
let simulationEndTime = null;
let warningTimers = [];


function showFreestyleLoadingPopup() {
  // Remove existing popup if any
  const existing = document.getElementById('freestyle-loading-popup');
  if (existing) existing.remove();

  // Create popup
  const popup = document.createElement('div');
  popup.id = 'freestyle-loading-popup';
  popup.style.cssText = `
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(0, 0, 0, 0.95);
    color: #0f0;
    padding: 30px 50px;
    border: 2px solid #0f0;
    border-radius: 8px;
    z-index: 999999;
    font-family: monospace;
    font-size: 18px;
    text-align: center;
    box-shadow: 0 0 20px rgba(0, 255, 0, 0.5);
  `;
  popup.innerHTML = `
    <div>⏳ Processing simulation results...</div>
    <div style="margin-top: 10px; font-size: 14px;">Please wait</div>
  `;
  document.body.appendChild(popup);
  console.log('✅ Freestyle loading popup displayed');
}

function broadcastLogoutToAllClients(resultsData = {}) {
  const portfolioId = localStorage.getItem('portfolioId') || resultsData.portfolioId || '';
  const enriched = { ...resultsData, portfolioId };

  try {
    localStorage.setItem('SIM_LOGOUT', JSON.stringify(enriched));
    setTimeout(() => localStorage.removeItem('SIM_LOGOUT'), 500);
  } catch {}

  try {
    const bc = new BroadcastChannel('sim-control');
    bc.postMessage({ type: 'logout', resultsData: enriched });
    bc.close();
  } catch {}
}




// ────── CORE TIMER FUNCTIONS ──────
export function initializeSimulationTimer(endTime, timerId) {
  const isSimulated = localStorage.getItem('isSimulated') === 'true';
  const simulationType = localStorage.getItem('simulationType');

  if (!isSimulated && !simulationType) {
    console.log('🛡️ Normal user detected — skipping timer initialization.');
    return;
  }

  const isMementoMori = simulationType === 'memento-mori';
  const isStorySimulation = simulationType === 'story-simulation';

  // Story Simulation uses its own phase-based timer (storyPhaseManager.js)
  if (isStorySimulation) {
    console.log('📖 Story Simulation detected - using phase-based timer instead');
    return; // Exit early - story simulation has its own timer system
  }

  // If no parameters provided, try to get from localStorage
  if (!endTime) {
    endTime = localStorage.getItem('simulationEndTime');
    timerId = localStorage.getItem('simulationTimerId');
    console.log('📦 Retrieved from localStorage:', { endTime, timerId });
  }

  // If still no endTime, check if we should show a demo timer for testing
  if (!endTime) {
    console.log('⚠️ No simulation timer data found');
    if (isSimulated) {
      console.log('🧪 Creating demo timer for testing (2 minutes)');
      endTime = new Date(Date.now() + 2 * 60 * 1000).toISOString(); // 2 minutes from now
      timerId = 'demo-timer-' + Date.now();
    }
  }

  simulationEndTime = new Date(endTime);

  console.log('⏰ Simulation timer initialized:', {
    endTime: simulationEndTime,
    timerId,
    timeRemaining: getTimeRemaining(),
    currentTime: new Date(),
    isMementoMori
  });

  clearSimulationTimers();
  createTimerDisplay(isMementoMori);
  updateTimerDisplay(isMementoMori);
  simulationTimer = setInterval(() => updateTimerDisplay(isMementoMori), 1000);
  scheduleWarnings();

  // Schedule freestyle LLM analysis pre-fetch (20s before end)
  if (simulationType === 'freestyle') {
    scheduleFreestyleLLMPreFetch();
  }

  localStorage.setItem('simulationEndTime', endTime);
  localStorage.setItem('simulationTimerId', timerId);
}

/**
 * Give the session more time.
 *
 * initializeSimulationTimer() already calls clearSimulationTimers() and rebuilds
 * the display, the countdown interval and the warning timeouts from scratch, so
 * re-running it with a later endTime is all an extension needs. Without this
 * the client would keep counting to the old deadline and end the session on
 * time regardless of what the server now says — the countdown is baked in at
 * page load and never re-polled.
 */
export function extendSimulationTimer(endTime, timerId) {
  if (!endTime) return false;
  const next = new Date(endTime);
  if (Number.isNaN(next.getTime())) return false;
  if (simulationEndTime && next <= simulationEndTime) return false;   // never shorten

  console.log('⏳ Simulation extended to', next.toISOString());
  initializeSimulationTimer(endTime, timerId || localStorage.getItem('simulationTimerId'));
  return true;
}

function getTimeRemaining() {
  if (!simulationEndTime) return 0;
  return Math.max(0, simulationEndTime.getTime() - Date.now());
}

function clearSimulationTimers() {
  if (simulationTimer) {
    clearInterval(simulationTimer);
    simulationTimer = null;
  }

  // Clear memento eulogy interval
  if (window.mementoEulogyInterval) {
    clearInterval(window.mementoEulogyInterval);
    window.mementoEulogyInterval = null;
  }

  warningTimers.forEach(timerId => clearTimeout(timerId));
  warningTimers = [];

  // Reset fetch guard
  eulogyFetchInProgress = false;
}

function createTimerDisplay(isMementoMori = false) {
  const existingTimer = document.getElementById('simulationTimer');
  if (existingTimer) existingTimer.remove();

  const timerDisplay = document.createElement('div');
  timerDisplay.id = 'simulationTimer';

  if (isMementoMori) {
    timerDisplay.className = 'simulation-timer-display memento-mori';
    timerDisplay.innerHTML = `
      <div>
        <div style="color:#ff4444; font-size:0.8rem; font-weight:bold;">
          Memento Mori Session Timer
        </div>
        <div id="simulationTimerText" style="font-size:.8rem; font-weight:bold; color:#00ff00;">
          --:--
        </div>
      </div>
    `;
  } else {
    timerDisplay.className = 'simulation-timer-display freestyle';
    timerDisplay.innerHTML = `
      <div id="simulationTimerText">
        Freestyle Session Timer: --:--
      </div>
    `;
  }

  document.body.appendChild(timerDisplay); // ✅ always append
}


function updateTimerDisplay(isMementoMori = false) {
  const timerText = document.getElementById('simulationTimerText');
  if (!timerText) return;

  const remaining = getTimeRemaining();
  const minutes = Math.floor(remaining / 60000);
  const seconds = Math.floor((remaining % 60000) / 1000);

  if (remaining <= 0) {
    timerText.innerHTML = isMementoMori
      ? `<span style="color:#ff0000;">Memento Mori Ended</span>`
      : `<span style="color:#ff0000;">Freestyle Session Ended</span>`;
    clearSimulationTimers();

    // ✅ CRITICAL: Re-read simulation type from localStorage at the moment of expiry
    // to ensure we have the most current value, but also use parameter as backup
    const currentSimulationType = localStorage.getItem('simulationType');
    const isMementoMoriFromStorage = (currentSimulationType === 'memento-mori');
    const isStorySimulationFromStorage = (currentSimulationType === 'story-simulation');

    // Use parameter as backup if localStorage is cleared (belt-and-suspenders approach)
    const finalIsMementoMori = isMementoMoriFromStorage || isMementoMori;
    const finalSimulationType = currentSimulationType || (isMementoMori ? 'memento-mori' : 'freestyle');

    console.log('⏰ [updateTimerDisplay] Timer expired, routing to correct ending:', {
      currentSimulationType,
      isMementoMoriFromStorage,
      isMementoMoriParam: isMementoMori,
      finalIsMementoMori,
      finalSimulationType,
      isStorySimulationFromStorage
    });

    handleSimulationEnd(finalSimulationType, finalIsMementoMori, isStorySimulationFromStorage);
    return;
  }

  // 🎨 Freestyle timer colors
  if (!isMementoMori) {
    let timeColor = '#fcba03';
    if (remaining <= 60000) timeColor = '#ff6b6b'; // last minute
    else if (remaining <= 120000) timeColor = '#ff8c00'; // last 2 min
    else if (remaining <= 300000) timeColor = '#ffd700'; // last 5 min

    timerText.style.color = timeColor;
    timerText.textContent = `Freestyle Session Timer: ${minutes}:${seconds
      .toString()
      .padStart(2, '0')}`;
  } else {
    // ⚰️ Memento Mori colors
    timerText.textContent = `${minutes}:${seconds
      .toString()
      .padStart(2, '0')}`;

    const timerElement = document.getElementById('simulationTimer');
    if (remaining <= 60000) {
      timerText.style.color = '#ff0000';
      timerElement.style.borderColor = '#ff0000';
      timerElement.style.boxShadow = '0 0 30px rgba(255,0,0,0.5)';
    } else if (remaining <= 180000) {
      timerText.style.color = '#ff6600';
      timerElement.style.borderColor = '#ff6600';
    } else {
      timerText.style.color = '#00ff00';
      timerElement.style.borderColor = '#ff6666';
    }
  }
}

/**
 * End the session now, by the participant's own choice — the same path as the
 * clock running out (results, end screen, logout), so nothing is left half
 * done in storage to trap them in an old session on this device.
 */
export function endSimulationNow() {
  clearSimulationTimers();
  const type = localStorage.getItem('simulationType');
  return handleSimulationEnd(type, type === 'memento-mori', type === 'story-simulation');
}

export function cleanupSimulationTimer() {
  clearSimulationTimers();

  const timerDisplay = document.getElementById('simulationTimer');
  if (timerDisplay) timerDisplay.remove();

  simulationEndTime = null;
  localStorage.removeItem('simulationEndTime');
  localStorage.removeItem('simulationTimerId');
}


function scheduleWarnings() {
  const timeRemaining = getTimeRemaining();
  // ✅ Capture simulation type at scheduling time to preserve it
  const simulationType = localStorage.getItem('simulationType');
  const isMementoMori = (simulationType === 'memento-mori');
  const isStorySimulation = (simulationType === 'story-simulation');

  console.log('📅 [scheduleWarnings] Scheduling warnings for simulation:', {
    simulationType,
    isMementoMori,
    isStorySimulation,
    timeRemaining,
    timeRemainingMinutes: Math.floor(timeRemaining / 60000)
  });

  // Schedule warnings at 5 min, 2 min, 1 min, and 30 seconds
  const warningTimes = [300000, 120000, 60000, 30000]; // milliseconds

  warningTimes.forEach(warningTime => {
    if (timeRemaining > warningTime) {
      const delay = timeRemaining - warningTime;
      const timerId = setTimeout(() => {
        showTimerWarning(warningTime);
      }, delay);
      warningTimers.push(timerId);
    }
  });

  // Schedule simulation end - PASS simulation type to preserve it
  if (timeRemaining > 0) {
    const endTimerId = setTimeout(() => {
      // ✅ Re-read localStorage at the moment of execution, with captured values as backup
      const currentSimulationType = localStorage.getItem('simulationType');
      const currentIsMementoMori = (currentSimulationType === 'memento-mori');
      const currentIsStorySimulation = (currentSimulationType === 'story-simulation');

      // ✅ CRITICAL: Use OR logic (not ternary) to detect from EITHER source
      // If EITHER the captured value OR the current localStorage says it's memento-mori, it is
      const finalSimulationType = currentSimulationType || simulationType;
      const finalIsMementoMori = currentIsMementoMori || isMementoMori;
      const finalIsStorySimulation = currentIsStorySimulation || isStorySimulation;

      console.log('⏰ [scheduleWarnings timeout] Simulation end triggered:', {
        capturedType: simulationType,
        currentType: currentSimulationType,
        finalType: finalSimulationType,
        finalIsMementoMori,
        finalIsStorySimulation
      });

      handleSimulationEnd(finalSimulationType, finalIsMementoMori, finalIsStorySimulation);
    }, timeRemaining);
    warningTimers.push(endTimerId);
  }
}

// ────── SCHEDULE FREESTYLE LLM PRE-FETCH (20s BEFORE END) ──────
function scheduleFreestyleLLMPreFetch() {
  const timeRemaining = getTimeRemaining();
  const preFetchTime = 20000; // 20 seconds before end

  if (timeRemaining > preFetchTime) {
    const delay = timeRemaining - preFetchTime;
    console.log(`⏰ [scheduleFreestyleLLMPreFetch] Scheduling LLM pre-fetch in ${Math.floor(delay / 1000)}s (20s before end)`);

    const preFetchTimerId = setTimeout(async () => {
      console.log('🤖 [Pre-fetch] Fetching LLM analysis 20s before timer ends...');

      try {
        const timerId = localStorage.getItem('simulationTimerId');
        const token = localStorage.getItem('token');

        if (!timerId || !token) {
          console.warn('⚠️ [Pre-fetch] Missing timerId or token');
          return;
        }

        // Fetch simulation results
        const resultsResponse = await fetch(`${apiBaseUrl}/api/sim/simulation/results/${timerId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!resultsResponse.ok) {
          console.warn('⚠️ [Pre-fetch] Failed to fetch simulation results:', resultsResponse.status);
          return;
        }

        const resultsData = await resultsResponse.json();
        const summary = resultsData?.summary || resultsData;

        // Cache the full live summary too: at T=0 the server logout may have
        // already deleted the portfolio, making the final results fetch come
        // back all-zeros. This snapshot from 20s before end is the fallback.
        if (summary && summary.finalPortfolio) {
          window.freestyleSummaryCache = summary;
          console.log('✅ [Pre-fetch] Live summary cached as end-of-sim fallback');
        }

        // Fetch LLM analysis
        const llmAnalysis = await fetchFreestyleLLMAnalysis(summary, token);

        if (llmAnalysis) {
          // Cache it globally (like memento-mori does with window.currentEulogyText)
          window.freestyleLLMAnalysis = llmAnalysis;
          console.log('✅ [Pre-fetch] LLM analysis cached:', llmAnalysis.substring(0, 100) + '...');
        }
      } catch (error) {
        console.error('❌ [Pre-fetch] Error fetching LLM analysis:', error);
      }
    }, delay);

    warningTimers.push(preFetchTimerId);
  } else {
    console.log(`⚠️ [scheduleFreestyleLLMPreFetch] Not enough time remaining (${Math.floor(timeRemaining / 1000)}s), skipping pre-fetch`);
  }
}

function showTimerWarning(timeLeft) {
  const minutes = Math.floor(timeLeft / 60000);
  const seconds = Math.floor((timeLeft % 60000) / 1000);
  
  let message;
  if (minutes > 0) {
    message = `${minutes} minute${minutes > 1 ? 's' : ''} remaining in simulation`;
  } else {
    message = `${seconds} seconds remaining in simulation`;
  }
  
  // Create warning popup
  const warning = document.createElement('div');
  warning.style.cssText = `
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(255, 107, 107, 0.95);
    color: white;
    padding: 20px 30px;
    border-radius: 10px;
    font-family: 'bithigh', monospace;
    font-size: 18px;
    font-weight: bold;
    z-index: 900;
    box-shadow: 0 0 20px rgba(255, 107, 107, 0.7);
    text-align: center;
    animation: pulse 0.5s ease-in-out;
  `;
  
  // Add CSS animation
  const style = document.createElement('style');
  style.textContent = `
    @keyframes pulse {
      0% { transform: translate(-50%, -50%) scale(0.8); opacity: 0; }
      50% { transform: translate(-50%, -50%) scale(1.1); opacity: 1; }
      100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
    }
  `;
  document.head.appendChild(style);
  
  warning.textContent = message;
  document.body.appendChild(warning);
  
  // Remove warning after 3 seconds
  setTimeout(() => {
    warning.remove();
    style.remove();
  }, 3000);
}

// ────── LOADING POPUP FOR EULOGY ──────
function showEulogyLoadingPopup() {
  const existing = document.getElementById('eulogy-loading-popup');
  if (existing) existing.remove();

  const popup = document.createElement('div');
  popup.id = 'eulogy-loading-popup';
  popup.innerHTML = `
    <div style="
      position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      background: rgba(0, 0, 0, 0.9);
      display: flex; flex-direction: column; justify-content: center; align-items: center;
      z-index: 10000; color: #fff; font-family: Georgia, serif;
    ">
      <div style="font-size: 28px; margin-bottom: 20px; color: #c9a227;">Your Time Has Come</div>
      <div style="font-size: 18px; margin-bottom: 30px; color: #ccc;">Composing your eulogy...</div>
      <div style="width: 200px; height: 4px; background: #333; border-radius: 2px; overflow: hidden;">
        <div id="eulogy-progress-bar" style="
          width: 0%; height: 100%;
          background: linear-gradient(90deg, #8b0000, #dc143c);
          transition: width 0.5s ease;
        "></div>
      </div>
      <div style="font-size: 14px; margin-top: 15px; color: #888;">Please wait...</div>
    </div>
  `;
  document.body.appendChild(popup);

  // Animate progress bar over 20 seconds
  setTimeout(() => {
    const bar = document.getElementById('eulogy-progress-bar');
    if (bar) {
      bar.style.width = '100%';
      bar.style.transition = 'width 19s linear';
    }
  }, 100);
}

// ────── PRE-FETCH EULOGY 15 SECONDS BEFORE END ──────
let eulogyFetchInProgress = false;
async function preFetchFinalEulogy() {
  if (eulogyFetchInProgress) {
    console.log('⏸️ [preFetch] Already fetching eulogy, skipping duplicate');
    return;
  }

  const objectiveId = localStorage.getItem('mementoMoriObjectiveId');
  const token = localStorage.getItem('token');

  if (!objectiveId) {
    console.warn('⚠️ [preFetch] No objective ID');
    return;
  }

  eulogyFetchInProgress = true;

  // Show loading indicator
  showEulogyLoadingPopup();

  try {
    console.log('🔄 [preFetch] Fetching eulogy from API (no timeout - LLM has 15s)...');
    const response = await fetch(`${apiBaseUrl}/api/memento-mori/current-eulogy`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
      },
      body: JSON.stringify({ objectiveId })
    });

    if (response.ok) {
      const data = await response.json();
      if (data.eulogyText) {
        window.currentEulogyText = data.eulogyText;
        console.log('✅ [preFetch] Eulogy cached:', data.eulogyText.substring(0, 80) + '...');
      }
    } else {
      console.warn('⚠️ [preFetch] API returned', response.status);
    }
  } catch (err) {
    console.warn('⚠️ [preFetch] Error:', err.message);
  } finally {
    eulogyFetchInProgress = false;
  }
}

// ────── HANDLE MEMENTO MORI END ──────
async function handleMementoMoriEnd() {
  console.log('⚰️ Memento Mori session ending...');

  // Clear eulogy interval immediately
  if (window.mementoEulogyInterval) {
    console.log('🗑️ Clearing memento eulogy interval');
    clearInterval(window.mementoEulogyInterval);
    window.mementoEulogyInterval = null;
  }
  window.mementoMoriInitialized = false;

  const FALLBACK_EULOGY = "Your journey through life's marketplace has concluded. In facing mortality, you have positioned yourself into your vision of Life Value.";

  // ✅ Show loading popup immediately so user knows we're working
  showEulogyLoadingPopup();

  try {
    const objectiveId = localStorage.getItem('mementoMoriObjectiveId');
    const token = localStorage.getItem('token');

    if (!objectiveId) {
      console.error('❌ No objective ID found - redirecting with fallback');
      redirectToMementoMoriEndPage({ eulogy: FALLBACK_EULOGY, portfolioInsights: [] });
      return;
    }

    // ✅ PRIORITY 1: Use cached eulogy from periodic fetches (instant, no LLM wait)
    let currentEulogy = window.currentEulogyText;
    console.log('📋 Cached eulogy available:', !!currentEulogy, currentEulogy ? `(${currentEulogy.length} chars)` : '');

    // ✅ PRIORITY 2: Fetch from API - user sees loading popup so we can wait longer (20s)
    if (!currentEulogy) {
      console.log('⏳ No cached eulogy, fetching from API (20s timeout - user sees loading)...');
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 20000); // 20s - user sees loading

        const response = await fetch(`${apiBaseUrl}/api/memento-mori/current-eulogy`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` })
          },
          body: JSON.stringify({ objectiveId }),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (response.ok) {
          const data = await response.json();
          currentEulogy = data.eulogyText;
          console.log('✅ API fetch succeeded:', currentEulogy?.substring(0, 80));
        }
      } catch (fetchError) {
        console.warn('⚠️ API fetch failed/timed out - using fallback:', fetchError.message);
      }
    }

    // ✅ PRIORITY 3: Use fallback if still nothing
    if (!currentEulogy) {
      console.warn('⚠️ No eulogy available, using fallback');
      currentEulogy = FALLBACK_EULOGY;
    }

    console.log('📜 Redirecting with eulogy:', currentEulogy.substring(0, 80) + '...');

    // Redirect with eulogy data
    redirectToMementoMoriEndPage({
      eulogy: currentEulogy,
      portfolioInsights: []
    });

  } catch (error) {
    console.error('❌ Error completing Memento Mori:', error);
    redirectToMementoMoriEndPage({
      eulogy: FALLBACK_EULOGY,
      portfolioInsights: []
    });
  }
}

// ────── REDIRECT TO MEMENTO MORI END PAGE ──────
function redirectToMementoMoriEndPage(eulogyData) {
  console.log('🪦 Redirecting to Memento Mori ending page...');
  try {
    localStorage.setItem('simulationEnding', 'true');
    localStorage.setItem('preventBotRecreation', 'true');

    localStorage.setItem('tempMementoMoriResults', JSON.stringify({
      ...eulogyData,
      timestamp: Date.now()
    }));
    console.log('💾 Stored eulogy data for ending page');
    
    // Redirect to memento mori ending page
    console.log('🎯 Final redirect to mementomoriend.html');
    window.location.replace('/mementomoriend.html');
  } catch (error) {
    console.error('❌ Error storing data:', error);
    // Try redirect anyway with flag set
    localStorage.setItem('simulationEnding', 'true');
    window.location.replace('/mementomoriend.html');
  }
}

async function handleSimulationEnd(simulationTypeParam = null, isMementoMoriParam = null, isStorySimulationParam = null) {
  console.log('🏁 [handleSimulationEnd] Simulation timer ended - determining route...');

  // ✅ CRITICAL ROUTING LOGIC: Check both parameters AND localStorage
  // Parameters are captured when timer starts, localStorage is current value
  const localStorageType = localStorage.getItem('simulationType');
  const simulationType = simulationTypeParam || localStorageType;

  // Calculate flags from BOTH sources and use OR logic for maximum detection
  const isMementoMoriFromParam = (isMementoMoriParam === true);
  const isMementoMoriFromType = (simulationType === 'memento-mori');
  const isMementoMori = isMementoMoriFromParam || isMementoMoriFromType;

  const isStorySimulationFromParam = (isStorySimulationParam === true);
  const isStorySimulationFromType = (simulationType === 'story-simulation');
  const isStorySimulation = isStorySimulationFromParam || isStorySimulationFromType;

  console.log('🔍 [handleSimulationEnd] ROUTING DECISION:', {
    '📦 Parameters': {
      simulationTypeParam,
      isMementoMoriParam,
      isStorySimulationParam
    },
    '💾 LocalStorage': {
      localStorageType
    },
    '✅ Final Decision': {
      simulationType,
      isMementoMori: isMementoMori ? ' YES - Route to mementomori.html' : 'no',
      isStorySimulation: isStorySimulation ? ' YES - Route to storysimend.html' : 'no',
      fallbackTo: (!isMementoMori && !isStorySimulation) ? '🎮 Freestyle - Route to freeend.html' : 'N/A'
    }
  });

  if (isMementoMori) {
    console.log('⚰️ Memento Mori timer expired - completing and redirecting to eulogy');
    await handleMementoMoriEnd();
    return; // Exit early - memento mori has its own flow
  }

  if (isStorySimulation) {
    console.log('📖 Story Simulation timer expired - handled by storyPhaseManager.js');
    return; // Exit early - story simulation uses phase-based timer
  }

  console.log('🎮 Freestyle simulation timer expired - redirecting to results');

  // ✅ Show loading popup FIRST (like memento-mori does)
  showFreestyleLoadingPopup();

  // Freestyle simulation logic
  try {
    const portfolioId = localStorage.getItem('portfolioId');
    const timerId = localStorage.getItem('simulationTimerId');
    const isSimulated = localStorage.getItem('isSimulated') === 'true';
    const token = localStorage.getItem('token');

    if (!portfolioId || !timerId) {
      console.error('❌ Missing portfolio ID or timer ID for simulation end');
      return;
    }

    // ────── CLOSE SHORT POSITIONS ──────
    console.log('📉 Closing all short positions for simulation end...');
    try {
      const portfolioResponse = await fetch(`${apiBaseUrl}/api/portfolios/${portfolioId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const portfolioData = await portfolioResponse.json();
      const shortPositions = portfolioData.shortPositions || [];

      for (const short of shortPositions) {
        try {
          await fetch(`${apiBaseUrl}/api/stocks/${short.stockId}/closeShortPosition`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ portfolioId })
          });
        } catch (error) {
          console.error(`⚠️ Error closing short for stock ${short.stockId}:`, error);
        }
      }
    } catch (error) {
      console.error('⚠️ Error closing short positions:', error);
    }

    // ────── FETCH SIMULATION RESULTS ──────
    let simulationResults = null;
    try {
      const token = localStorage.getItem('token');

      // Try both possible backend mount paths for compatibility
      let response = await fetch(`${apiBaseUrl}/api/sim/simulation/results/${timerId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        console.warn('⚠️ Primary results route failed, retrying alternate path...');
        response = await fetch(`${apiBaseUrl}/api/simulation/results/${timerId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }

      if (response.ok) {
        const data = await response.json();
        // Backend returns { summary: {...} } - keep the wrapper structure
        simulationResults = data;
        console.log('✅ Successfully fetched simulation results');
      } else {
        console.error('❌ Failed to fetch simulation results (status:', response.status, ')');
        simulationResults = { summary: createDefaultResults?.() || getDefaultResultsFallback() };
      }
    } catch (error) {
      console.error('💥 Error fetching simulation results:', error);
      simulationResults = { summary: createDefaultResults?.() || getDefaultResultsFallback() };
    }

    // ────── USE CACHED LLM ANALYSIS (PRE-FETCHED 20s BEFORE END) ──────
    let summary = simulationResults?.summary || simulationResults;

    // If the server already cleaned up the portfolio, the fetched summary is
    // all zeros ("Ended With: Nothing"). Prefer the live snapshot cached 20s
    // before the end — it has the real holdings detail.
    const summaryLooksEmpty = (s) => {
      const fp = s?.finalPortfolio || {};
      return !fp.totalValue && !fp.points && !fp.stocks && !fp.items && !fp.zollars && !fp.bonds;
    };
    if (summaryLooksEmpty(summary) && window.freestyleSummaryCache && !summaryLooksEmpty(window.freestyleSummaryCache)) {
      console.log('♻️ Fetched summary is empty (portfolio already cleaned up) — using pre-fetched live snapshot');
      summary = window.freestyleSummaryCache;
    }

    let llmAnalysis = window.freestyleLLMAnalysis || null;

    if (llmAnalysis) {
      console.log('✅ Using pre-fetched LLM analysis from cache');
    } else if (token) {
      console.log('⚠️ No cached LLM analysis, fetching now (fallback)...');
      llmAnalysis = await fetchFreestyleLLMAnalysis(summary, token);
    }

    // ────── REDIRECT TO FREEEND.HTML WITH COMPLETE DATA ──────
    await redirectToFreeendWithResults(summary, portfolioId, timerId, llmAnalysis);

  } catch (error) {
    console.error('💥 Error handling simulation end:', error);
    await redirectToFreeendWithResults(getDefaultResultsFallback(), null, null, null);
  }
}

// ────── LOCAL DEFAULT RESULTS FALLBACK ──────
function getDefaultResultsFallback() {
  return {
    performance: 'Complete',
    duration: 10,
    netGain: 0,
    tradesCompleted: 0,
    socialInteractions: 0,
    finalPortfolio: {
      points: 0,
      totalValue: 0,
      stocks: 0,
      items: 0,
      zollars: 0
    }
  };
}


// ────── FETCH LLM ANALYSIS WHILE STILL AUTHENTICATED ──────
async function fetchFreestyleLLMAnalysis(summary, authToken) {
  if (!authToken) {
    console.warn('⚠️ No auth token for LLM analysis');
    return null;
  }

  try {
    console.log('🤖 Fetching LLM analysis BEFORE redirect (30s timeout)...');
    console.log('📊 [LLM Request] Summary data being sent:', {
      netGain: summary?.netGain,
      tradesCompleted: summary?.tradesCompleted,
      socialInteractions: summary?.socialInteractions,
      duration: summary?.duration,
      hasSensitivitySignals: !!summary?.sensitivitySignals,
      sensitivitySignals: summary?.sensitivitySignals
    });

    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      console.warn('⏱️ LLM analysis request timed out after 30 seconds');
      controller.abort();
    }, 30000);

    // style 'news': the end screen shows this as THE ZEXCHANGE TIMES item.
    const response = await fetch(`${apiBaseUrl}/api/sim/simulation/analysis`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      // The dilemma choices made on the floor (zfloor.js checkDilemma) — so
      // the news says what they chose, not only what they held.
      body: JSON.stringify({ summary: { ...summary, lessons: readFloorList('lessons'), endings: readFloorList('endings') }, style: 'news', lang: localStorage.getItem('preferredLanguage') || 'en' }),
      credentials: 'include',
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (response.ok) {
      const data = await response.json();
      console.log('✅ LLM analysis pre-fetched:', data.paragraph?.substring(0, 100) + '...');
      return data.paragraph || null;
    } else {
      console.warn('⚠️ LLM analysis API returned', response.status);
      return null;
    }
  } catch (error) {
    console.warn('⚠️ Error pre-fetching LLM analysis:', error.message);
    return null;
  }
}

// ────── REDIRECT TO FREEEND WITH RESULTS ──────
async function redirectToFreeendWithResults(summary, portfolioId, timerId, llmAnalysis = null) {
  const isSimulated = localStorage.getItem('isSimulated') === 'true';
  const simulationType = localStorage.getItem('simulationType');
  const authToken = localStorage.getItem('token');

  // ✅ Guard: only simulations should ever run this redirect
  if (!isSimulated && !simulationType) {
    console.log('🛡️ Normal user detected — skipping redirectToFreeendWithResults entirely.');
    return;
  }

  console.log('🔄 Preparing redirect to freeend.html with simulation results');

  const pid = portfolioId || localStorage.getItem('portfolioId') || '';
  const tid = timerId || localStorage.getItem('simulationTimerId') || '';

  try {
    // Build the results payload with pre-fetched LLM analysis
    const resultsData = {
      summary: summary || createDefaultResults?.() || {},
      portfolioId: pid,
      timerId: tid,
      timestamp: Date.now(),
      llmAnalysis: llmAnalysis // ✅ Include pre-generated analysis (passed from caller)
    };

    // Keep a one-time copy of the auth token so the results page can
    // refresh data/LLM without leaking it via the URL backup.
    if (authToken) {
      localStorage.setItem('tempSimulationToken', authToken);
    }

    // Store in session/local for redundancy
    console.log('💾 Storing results in sessionStorage & localStorage...');
    try { sessionStorage.setItem('simulationResults', JSON.stringify(resultsData)); } catch {}
    try { localStorage.setItem('tempSimulationResults', JSON.stringify(resultsData)); } catch {}

    // The results travel in storage, not in the address. Packing them into the
    // URL made a ~16,000-character address once the end screen gained the
    // verdict, flows and room ranking, and Safari refused to open it ("Safari
    // cannot open the page"). btoa() also throws on any character outside
    // Latin-1, such as the em dashes in the written analysis. freeend.js reads
    // tempSimulationResults first, so the short address loses nothing.
    const endUrl = endPageUrl(resultsData);

    // Mark simulation ending before anything else
    localStorage.setItem('simulationEnding', 'true');
    localStorage.setItem('preventBotRecreation', 'true');

    // Wait to ensure storage writes complete and verify data
    await new Promise(r => setTimeout(r, 800));

    // ✅ Verify data was actually stored before redirecting
    const verifyStored = localStorage.getItem('tempSimulationResults');
    if (!verifyStored) {
      console.warn('⚠️ Data verification failed, retrying storage...');
      try { localStorage.setItem('tempSimulationResults', JSON.stringify(resultsData)); } catch {}
      await new Promise(r => setTimeout(r, 400));
    }
    console.log('✅ Data storage verified successfully');

    // Fan-out to other tabs if needed
    try {
      if (isSimulated && typeof broadcastLogoutToAllClients === 'function') {
        broadcastLogoutToAllClients({ ...resultsData, portfolioId: pid });
      } else {
        console.log('🛡️ Skipping broadcast logout for non-sim users.');
      }
    } catch (e) {
      console.warn('broadcastLogoutToAllClients failed:', e);
    }

    // Notify local listeners
    try {
      window.dispatchEvent(new CustomEvent('simulation-ended', { detail: { payload: { ...resultsData, portfolioId: pid } } }));
    } catch {}

    // ✅ Immediate redirect first — before any logout
    console.log('🎯 Redirecting immediately to freeend.html');
    window.location.replace(endUrl);

    // ───────────────────────────────
    // ⏳ AFTER REDIRECT: run logout async in background (10s delay to allow LLM response)
    // ───────────────────────────────
    setTimeout(async () => {
      try {
        const simulationType = localStorage.getItem('simulationType');
        const isSimulated = localStorage.getItem('isSimulated') === 'true';

        // Skip server logout for freestyle to avoid redirect override
        if (simulationType === 'freestyle' || simulationType === 'free-style') {
          console.log('🛡️ Freestyle simulation - skipping blocking logout.');
          return;
        }

        if (isSimulated || simulationType) {
          console.log('🚪 Performing async logout after redirect (10s delay)...');
          const logoutPromise = (typeof logout === 'function') ? logout() : Promise.resolve();
          await Promise.race([
            logoutPromise,
            new Promise(r => setTimeout(r, 2000))
          ]);
        }
      } catch (e) {
        console.warn('Async logout failed silently (ignored):', e);
      }
    }, 10000); // ✅ Changed from 2000ms to 10000ms (10 seconds)

  } catch (error) {
    console.error('Error during redirect to freeend:', error);
    // Emergency fallback
    const fallbackData = {
      summary: summary || (typeof createDefaultResults === 'function' ? createDefaultResults() : {}),
      portfolioId: pid,
      timerId: tid,
      timestamp: Date.now()
    };
    try { localStorage.setItem('tempSimulationResults', JSON.stringify(fallbackData)); } catch {}
    window.location.replace(endPageUrl(fallbackData));
  }
}


// The end page's address. Results ride along in the URL only when they are
// small enough for every browser and encodable; otherwise storage carries them.
function endPageUrl(data) {
  try {
    const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(data))));
    if (encoded.length <= 1500) return `/freeend.html?data=${encoded}`;
  } catch { /* storage has it */ }
  return '/freeend.html';
}

function createDefaultResults() {
    return {
        performance: 'Complete',
        duration: 10,
        netGain: 0,
        tradesCompleted: 0,
        socialInteractions: 0,
        finalPortfolio: {
            points: 0,
            totalValue: 0,
            stocks: 0,
            items: 0,
            zollars: 0,
            bonds: 0
        },
        improvements: []
    };
}


// ────── LOGOUT FUNCTION ──────
async function logout() {
  console.log('[logout()] CALLED', { 
    isSimulated: localStorage.getItem('isSimulated'),
    simulationType: localStorage.getItem('simulationType'),
    simulationEnding: localStorage.getItem('simulationEnding')
  });
  console.trace('[logout()] stack');
   const portfolioId = localStorage.getItem('portfolioId');
   const token = localStorage.getItem('token');
   const isSimulated = localStorage.getItem('isSimulated') === 'true';
   const isMementoMori = localStorage.getItem('simulationType') === 'memento-mori';

   // 🛡️ Preserve Memento Mori eulogy data before cleanup
   const preservedEulogy = localStorage.getItem('tempMementoMoriResults');
 
   // ADD THIS BLOCK - Visual feedback immediately
   const logoutButton = document.getElementById('logOut');
   const originalText = logoutButton ? logoutButton.textContent : 'Logout';
   if (logoutButton) {
   logoutButton.textContent = 'Logging out...';
   logoutButton.disabled = true;
   logoutButton.style.opacity = '0.6';
   logoutButton.style.pointerEvents = 'none';
   }
 
   // Show a processing overlay
   const overlay = document.createElement('div');
   overlay.style.cssText = `
   position: fixed;
   top: 0;
   left: 0;
   width: 100%;
   height: 100%;
   background: rgba(0, 0, 0, 0.8);
   z-index: 10000;
   display: flex;
   align-items: center;
   justify-content: center;
   color: #00ff00;
   font-family: 'bithigh', Arial, sans-serif;
   font-size: 12px;
   `;
   overlay.textContent = 'Disconnecting...';
   document.body.appendChild(overlay);
 
   if (!portfolioId || !token) {
   console.error('Portfolio ID or token not found in local storage');
   if (overlay) overlay.remove();
   if (logoutButton) {
     logoutButton.textContent = originalText;
     logoutButton.disabled = false;
     logoutButton.style.opacity = '1';
     logoutButton.style.pointerEvents = 'auto';
   }
   return;
   }
   try {
     // First, disconnect all bot sockets if this is a simulated user
     if (isSimulated && socket && socket.connected) {
       console.log('🤖 Disconnecting bots for simulated user logout...');
       socket.emit('disconnectUserBots', { portfolioId });
       
       // Wait a moment for bot disconnection to complete
       await new Promise(resolve => setTimeout(resolve, 1000));
     }
 
     // Update the active status to false on the server
     const response = await fetch(`${apiBaseUrl}/api/portfolios/${portfolioId}`, {
       method: 'PATCH',
       headers: {
         'Content-Type': 'application/json',
         'Authorization': `Bearer ${token}`,
       },
       body: JSON.stringify({ active: false }),
     });
 
     if (!response.ok) {
       throw new Error('Failed to update active status during logout');
     }
 
   } catch (error) {
     console.error('Error updating active status during logout:', error);
     showNotification('Error updating active status during logout. Please try again.', false);
   }
 
   try {
     // Call the logout endpoint on the server
     const logoutResponse = await fetch(`${apiBaseUrl}/api/logout`, {
       method: 'POST',
       headers: {
         'Content-Type': 'application/json',
         'Authorization': `Bearer ${token}`,
       },
       body: JSON.stringify({ portfolioId, isSimulated }),
       credentials: 'include'
     });
 
     if (!logoutResponse.ok) {
       throw new Error('Failed to logout from server');
     }
 
     const logoutData = await logoutResponse.json();
     console.log(logoutData.message);
     
     // Show success message for simulated users
     if (isSimulated) {
       console.log('🎯 Simulation cleanup completed successfully');
     }
     
   } catch (error) {
     console.error('Error during server logout:', error);
     showNotification('Error during server logout. Please try again.', false);
   }
   
   const simulationEnding = localStorage.getItem('simulationEnding') === 'true';
   console.log(`🔍 [Logout] simulationEnding flag captured: ${simulationEnding}`);

   // Disconnect main socket
   if (socket && socket.connected) {
     console.log('🔌 Disconnecting main socket...');
     socket.disconnect();
   }
   
   if (simulationEnding || isSimulated) {
     cleanupSimulationTimer();
   }
   if (isMementoMori) {
     cleanupMementoMoriTimer?.();
   }

  // Clear local storage and redirect to the login page
  localStorage.removeItem('portfolioId');
  localStorage.removeItem('token');
  localStorage.removeItem('role');
  localStorage.removeItem('sessionId');
  localStorage.removeItem('socketId');
  localStorage.removeItem('initialPortfolioState');
  localStorage.removeItem('selectedItems');
  localStorage.removeItem('superUserId');
  localStorage.removeItem('isSimulated'); 
  localStorage.removeItem('simulatedCompany');
  localStorage.removeItem('firstLogin');
  localStorage.removeItem('activeMementoMori');
  localStorage.removeItem('mementoMoriChecked');
  localStorage.removeItem('mementoMoriStarted');
  // ⚠️ CRITICAL: Only remove simulationType if NOT in simulation ending flow
  // (Otherwise it breaks the redirect detection on the end page)
  if (!simulationEnding) {
    localStorage.removeItem('simulationType');
    console.log('🗑️ Removed simulationType (normal logout)');
  } else {
    console.log('💾 Preserved simulationType for simulation end page');
  }
  localStorage.removeItem('mementoMoriObjectiveId');
  localStorage.removeItem('mementoMoriMode');
  localStorage.removeItem('mementoShowAllCompanies');
  localStorage.removeItem('stockBidData');
  localStorage.removeItem('tradeStory');
  localStorage.removeItem('tradeStoryTimestamp');
  localStorage.removeItem('lastConnectedStableKey');
  localStorage.removeItem('simulationEndTime');
  localStorage.removeItem('simulationTimerId');
  localStorage.removeItem('simulationEnding');
  localStorage.removeItem('botTradeOffer');
  localStorage.removeItem('botTradeRecommendation');
  localStorage.removeItem('tradingRecommendation');
  localStorage.removeItem('botTradeTimestamp');
  localStorage.removeItem('psychologyAnalysis');
  localStorage.removeItem('preventBotRecreation');
  localStorage.removeItem('preferredLanguage');
  
   // 🔄 NEW — wipe any key that starts with "trade_"
     for (let i = localStorage.length - 1; i >= 0; i--) {
       const key = localStorage.key(i);
       if (key && key.startsWith('trade_')) {
       localStorage.removeItem(key);
       }
     }
  
     // ✅ Restore preserved eulogy after cleanup
     if (preservedEulogy) {
       localStorage.setItem('tempMementoMoriResults', preservedEulogy);
       console.log('💾 Preserved tempMementoMoriResults through logout');
     }
     
     // Clear cookies related to the session (if applicable)
     document.cookie = 'token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
     document.cookie = 'sessionId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
     document.cookie = 'portfolioId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
   
     if (!simulationEnding) {
        window.location.href = '/index.html';
      }
  
     // 🧹 Cleanup overlay & restore button state
     if (overlay) overlay.remove();
     if (logoutButton) {
       logoutButton.textContent = originalText;
       logoutButton.disabled = false;
       logoutButton.style.opacity = '1';
       logoutButton.style.pointerEvents = 'auto';
     }
   }
// ────── SOCKET LISTENER FOR SERVER-TRIGGERED END ──────
// This handles the case where the server ends the simulation via checkExpiredSimulations
function setupSimulationSocketListeners() {
  if (!socket) {
    console.log('⚠️ Socket not available for simulation listeners');
    return;
  }

  // Remove any existing listener to prevent duplicates
  socket.off('simulation:ended');

  socket.on('simulation:ended', async (payload) => {
    console.log('📡 [Socket] Received simulation:ended from server:', payload);

    const { summary, portfolioId, timerId } = payload;

    // Only process if this is for our portfolio
    const myPortfolioId = localStorage.getItem('portfolioId');
    if (portfolioId && myPortfolioId && portfolioId !== myPortfolioId) {
      console.log('⏭️ Ignoring simulation:ended for different portfolio');
      return;
    }

    // Check simulation type to route correctly
    const simulationType = localStorage.getItem('simulationType');
    const isMementoMori = (simulationType === 'memento-mori');
    const isStorySimulation = (simulationType === 'story-simulation');

    if (isMementoMori) {
      console.log('⚰️ Server ended Memento Mori - handled separately');
      return; // Memento Mori has its own end flow
    }

    if (isStorySimulation) {
      console.log('📖 Server ended Story Simulation - handled separately');
      return;
    }

    // Freestyle: use server-provided summary if available
    if (summary && Object.keys(summary).length > 0) {
      console.log('✅ Using server-provided summary for redirect');
      // Prefer the pre-fetched live snapshot if the payload summary is empty
      const fp = summary.finalPortfolio || {};
      const payloadEmpty = !fp.totalValue && !fp.points && !fp.stocks && !fp.items && !fp.zollars && !fp.bonds;
      const bestSummary = (payloadEmpty && window.freestyleSummaryCache) ? window.freestyleSummaryCache : summary;
      await redirectToFreeendWithResults(bestSummary, portfolioId, timerId, window.freestyleLLMAnalysis || null);
    } else {
      console.log('⚠️ No summary in payload, triggering normal end flow');
      handleSimulationEnd('freestyle', false, false);
    }
  });

  socket.on('forceLogout', (payload) => {
    console.log('📡 [Socket] Received forceLogout:', payload);
    // If we're already on an end page, ignore
    if (window.location.pathname.includes('freeend') ||
        window.location.pathname.includes('mementomoriend') ||
        window.location.pathname.includes('storysimend')) {
      console.log('⏭️ Already on end page, ignoring forceLogout');
      return;
    }
    // Otherwise let normal flow handle it
  });

  console.log('✅ Simulation socket listeners registered');
}

// ────── AUTO-INITIALIZE ON PAGE LOAD (SAFE VERSION) ──────
 document.addEventListener('DOMContentLoaded', () => {
   console.log('🚀 SimulationTimer module loaded (safe mode)');

   const simulationEndTime = localStorage.getItem('simulationEndTime');
   const simulationTimerId = localStorage.getItem('simulationTimerId');
   const isSimulated = localStorage.getItem('isSimulated') === 'true';
   const simulationType = localStorage.getItem('simulationType');

   // Setup socket listeners for server-triggered simulation end
   // Wait a moment for socket to be ready
   setTimeout(() => {
     setupSimulationSocketListeners();
   }, 1000);

   // 🛡️ Only proceed for simulations
   if (!isSimulated && !simulationType) {
     console.log('🛑 Normal user detected — skipping simulation timer entirely.');
 
     // Clean up any leftover visual timer from previous sessions
     const strayTimer = document.getElementById('simulationTimer');
     if (strayTimer) {
       console.log('🧹 Removing stray timer display for normal user.');
       strayTimer.remove();
     }
     return;
   }
 
   if (simulationEndTime && simulationTimerId) {
     const endTime = new Date(simulationEndTime);
     if (endTime instanceof Date && !isNaN(endTime)) {
       if (endTime > new Date()) {
         console.log('🔄 Restoring simulation timer from localStorage');
         initializeSimulationTimer(simulationEndTime, simulationTimerId);
       } else {
         console.log('⏰ Timer expired, cleaning up');
         localStorage.removeItem('simulationEndTime');
         localStorage.removeItem('simulationTimerId');
       }
     } else {
       console.warn('⚠️ Invalid simulationEndTime detected — cleaning up.');
       localStorage.removeItem('simulationEndTime');
       localStorage.removeItem('simulationTimerId');
     }
   } else {
     console.log('🧘 No simulation data yet — timer module idle.');
   }
 });


// ────── TESTING FUNCTIONS ──────
// Add global test functions for debugging
window.testSimulationTimer = function(minutes = 2) {
  console.log(`🧪 Starting test timer for ${minutes} minutes`);
  const endTime = new Date(Date.now() + minutes * 60 * 1000).toISOString();
  const timerId = 'test-timer-' + Date.now();
  initializeSimulationTimer(endTime, timerId);
};

window.clearSimulationTimer = function() {
  console.log('🧹 Clearing simulation timer');
  cleanupSimulationTimer();
};

window.checkTimerStatus = function() {
  console.log('📊 Timer Status:', {
    simulationEndTime,
    timeRemaining: getTimeRemaining(),
    timerExists: !!document.getElementById('simulationTimer'),
    localStorage: {
      endTime: localStorage.getItem('simulationEndTime'),
      timerId: localStorage.getItem('simulationTimerId')
    }
  });
};

let scrollTimeout;
window.addEventListener('scroll', () => {
  const timer = document.getElementById('simulationTimer');
  if (timer) {
    if (window.scrollY > 50) {
      timer.classList.add('scrolled');
    } else {
      timer.classList.remove('scrolled');
    }
  }
});

function readFloorList(key) {
  try {
    const pid = localStorage.getItem('portfolioId');
    return JSON.parse(localStorage.getItem(`zxGoal_${pid}_${key}`) || '[]');
  } catch { return []; }
}
